import type { Coach, CoachTable } from "@/types/coach";
import { ApiResponse, User } from "@/types";

import { apiSlice } from "@/redux/apiSlice";
const { VITE_DEFAULT_ORG } = import.meta.env;
type Users = User & { own_coach_id: string };
interface CoachQuery {
  query: string;
  org_id: number;
}

export const CoachApi = apiSlice.injectEndpoints({
  endpoints(builder) {
    return {
      AddCoach: builder.mutation<ApiResponse, Omit<Coach, 'id'>>({
        query: (coachdata) => ({
          url: "/coach",
          method: "POST",
          body: coachdata,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }),
        invalidatesTags: ["Coaches"],
      }),
      getCoachCount: builder.query<{ total_coaches: number }, number>({
        query: (org_id) => ({
          url: `/coach/count/${org_id}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Coaches"],
      }),
      getCoaches: builder.query<CoachTable, CoachQuery>({
        query: (searchCretiria) => ({
          url: `/coach?org_id=${searchCretiria.org_id}&${searchCretiria.query}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Coaches"],
      }),
      deleteCoach: builder.mutation<ApiResponse, { org_id: number; id: number }>({
        query: (coach) => ({
          url: `/coach/${coach.id}?org_id=${coach.org_id}`,
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }),
        invalidatesTags: ["Coaches"],
      }),
      updateCoach: builder.mutation<ApiResponse, Coach & { id: number }>({
        query: (coachdata) => ({
          url: `/coach/${coachdata.id}`,
          method: "PUT",
          body: coachdata,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }),
        invalidatesTags: ["Coaches"],
      }),
      getCoachById: builder.query<Coach, { org_id: number; id: number }>({
        query: (coach) => ({
          url: `/coach/${coach.id}?org_id=${coach.org_id}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Coaches"],
      }),
      getCoachList: builder.query<{ value: number; label: string }[], number>({
        query: (org_id) => ({
          url: `/coach/list/${org_id}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        transformResponse: (response: { id: number; name: string }[]) => {
          return response.map((item) => ({
            value: item.id,
            label: item.name,
          }));
        },
        providesTags: ["Coaches"],
      }),
      getCoachInSystem: builder.query<
        Users[],
        {
          query: string;
          limit?: number;
          offset?: number;
          sort_order?: string;
          sort_key?: string;
          type?: string;
        }
      >({
        query: (coachdata) => {
          const params = new URLSearchParams();
          params.append("org_id", VITE_DEFAULT_ORG);

          // If search query is empty, use default filters
          if (!coachdata.query || coachdata.query.trim() === "") {
            if (coachdata.limit)
              params.append("limit", coachdata.limit.toString());
            if (coachdata.offset)
              params.append("offset", coachdata.offset.toString());
            if (coachdata.sort_order)
              params.append("sort_order", coachdata.sort_order);
            if (coachdata.sort_key)
              params.append("sort_key", coachdata.sort_key);
            if (coachdata.type) params.append("type", coachdata.type);
          } else {
            // If search query exists, only use search_key
            params.append("search_key", coachdata.query);
          }

          return {
            url: `/coach?${params.toString()}`,
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          };
        },
        transformResponse: (response: { data: Users[] }) => response.data,
        providesTags: ["Coaches"],
      }),
      getAllCoachForExport: builder.query<CoachTable, { org_id: number; filterQuery?: string }>({
        query: ({ org_id, filterQuery }) => ({
          url: `/coach?org_id=${org_id}&offset=0&sort_key=id&sort_order=desc${filterQuery ? `&${filterQuery}` : ''}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Coaches"],
      }),
    };
  },
});

export const {
  useAddCoachMutation,
  useGetCoachCountQuery,
  useGetCoachesQuery,
  useDeleteCoachMutation,
  useUpdateCoachMutation,
  useGetCoachByIdQuery,
  useGetCoachListQuery,
  useGetCoachInSystemQuery,
  useLazyGetAllCoachForExportQuery,
} = CoachApi;
